"use client"

import { useState, useEffect, useRef } from "react"
import { ProductCard } from "./product-card"
import type { Product } from "@/lib/types"
import { apiStore } from "@/lib/api-store"
import { ChevronLeft, ChevronRight } from "lucide-react"

interface ProductCarouselProps {
    title?: string
    autoplayInterval?: number
}

export function ProductCarousel({
    title = "Produits du moment",
    autoplayInterval = 3000
}: ProductCarouselProps) {
    const [products, setProducts] = useState<Product[]>([])
    const [currentIndex, setCurrentIndex] = useState(0)
    const [isHovered, setIsHovered] = useState(false)
    const [loading, setLoading] = useState(true)
    const [itemsPerView, setItemsPerView] = useState(4)
    const intervalRef = useRef<NodeJS.Timeout | null>(null)

    // Gestion du responsive
    useEffect(() => {
        const updateItemsPerView = () => {
            if (window.innerWidth < 640) {
                setItemsPerView(1)
            } else if (window.innerWidth < 1024) {
                setItemsPerView(2)
            } else if (window.innerWidth < 1280) {
                setItemsPerView(3)
            } else {
                setItemsPerView(4)
            }
        }

        updateItemsPerView()
        window.addEventListener('resize', updateItemsPerView)
        return () => window.removeEventListener('resize', updateItemsPerView)
    }, [])

    useEffect(() => {
        loadRandomProducts()
    }, [])

    useEffect(() => {
        if (!isHovered && products.length > itemsPerView) {
            startAutoplay()
        } else {
            stopAutoplay()
        }

        return () => stopAutoplay()
    }, [isHovered, products.length, currentIndex, itemsPerView])

    const loadRandomProducts = async () => {
        try {
            setLoading(true)
            const allProducts = await apiStore.getProducts()

            // Mélanger les produits de manière aléatoire
            const shuffled = [...allProducts].sort(() => Math.random() - 0.5)

            // Prendre 12 produits aléatoires
            const randomProducts = shuffled.slice(0, 12)

            setProducts(randomProducts)
        } catch (error) {
            console.error("Erreur lors du chargement des produits:", error)
        } finally {
            setLoading(false)
        }
    }

    const startAutoplay = () => {
        stopAutoplay()
        intervalRef.current = setInterval(() => {
            setCurrentIndex((prev) => {
                const maxIndex = Math.max(0, products.length - itemsPerView)
                return prev >= maxIndex ? 0 : prev + 1
            })
        }, autoplayInterval)
    }

    const stopAutoplay = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
        }
    }

    const goToNext = () => {
        const maxIndex = Math.max(0, products.length - itemsPerView)
        setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1))
    }

    const goToPrevious = () => {
        const maxIndex = Math.max(0, products.length - itemsPerView)
        setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1))
    }

    if (loading) {
        return (
            <div className="py-12 bg-gradient-to-b from-primary/5 to-background">
                <div className="container">
                    <h2 className="text-3xl font-bold mb-8 text-center">{title}</h2>
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                    </div>
                </div>
            </div>
        )
    }

    if (products.length === 0) {
        return null
    }

    return (
        <div className="py-12 bg-gradient-to-b from-primary/5 to-background">
            <div className="container">
                <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
                    {title}
                </h2>

                <div
                    className="relative group"
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    {/* Navigation Buttons */}
                    {products.length > itemsPerView && (
                        <>
                            <button
                                onClick={goToPrevious}
                                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 sm:-translate-x-4 z-10 bg-white dark:bg-gray-800 shadow-lg rounded-full p-2 sm:p-3 hover:bg-primary hover:text-white transition-all opacity-0 group-hover:opacity-100"
                                aria-label="Précédent"
                            >
                                <ChevronLeft size={20} className="sm:w-6 sm:h-6" />
                            </button>
                            <button
                                onClick={goToNext}
                                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 sm:translate-x-4 z-10 bg-white dark:bg-gray-800 shadow-lg rounded-full p-2 sm:p-3 hover:bg-primary hover:text-white transition-all opacity-0 group-hover:opacity-100"
                                aria-label="Suivant"
                            >
                                <ChevronRight size={20} className="sm:w-6 sm:h-6" />
                            </button>
                        </>
                    )}

                    {/* Carousel Container */}
                    <div className="overflow-hidden">
                        <div
                            className="flex transition-transform duration-500 ease-in-out gap-4 sm:gap-6"
                            style={{
                                transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)`
                            }}
                        >
                            {products.map((product) => (
                                <div
                                    key={product._id || product.id}
                                    className="flex-shrink-0 w-full sm:w-1/2 lg:w-1/3 xl:w-1/4"
                                    style={{
                                        width: `calc(${100 / itemsPerView}% - ${((itemsPerView - 1) * (itemsPerView === 1 ? 16 : 24)) / itemsPerView}px)`
                                    }}
                                >
                                    <ProductCard product={product} />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Indicators */}
                    {products.length > itemsPerView && (
                        <div className="flex justify-center gap-2 mt-6">
                            {Array.from({ length: Math.max(1, products.length - itemsPerView + 1) }).map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentIndex(index)}
                                    className={`h-2 rounded-full transition-all ${currentIndex === index
                                            ? "w-8 bg-primary"
                                            : "w-2 bg-gray-300 hover:bg-gray-400"
                                        }`}
                                    aria-label={`Aller à la position ${index + 1}`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
